// The Capability map's PERMISSION LEDGER (Capability Map v2, the "Signal" re-layout): one card stating
// that everything Ritona can reach sits in one of three boxes, with the proportion bar and one tile per
// bucket — the serif count, its share, and the plain-English restatement of the recorded flags. A tile
// toggles the bucket filter (client view-state only); the counts always cover the WHOLE map, so the
// ledger stays the honest totals line even while filtered (G-182). Tokens only (G-181).

import type { CapabilityEntryView } from '../operate-projections.ts';
import {
  BUCKET_META,
  BUCKET_ORDER,
  bucketCounts,
  type CapabilityBucket,
} from './capability-buckets.ts';

/** One bucket tile — the count, share, and plain-English note; pressing it toggles the filter. */
function BucketTile({
  bucket,
  count,
  total,
  on,
  onToggle,
}: {
  readonly bucket: CapabilityBucket;
  readonly count: number;
  readonly total: number;
  readonly on: boolean;
  readonly onToggle: () => void;
}): React.JSX.Element {
  const meta = BUCKET_META[bucket];
  return (
    <button
      type="button"
      className="cm-tile"
      data-bucket={bucket}
      aria-pressed={on}
      onClick={onToggle}
    >
      <span className="cm-tile-id">
        <span className="dot" aria-hidden="true" />
        <span className="lb">{meta.label}</span>
      </span>
      <span className="cm-tile-count">
        <span className="n">{count}</span>
        {total > 0 ? <span className="pct">{Math.round((count / total) * 100)}%</span> : null}
      </span>
      <span className="cm-tile-note">{meta.note}</span>
    </button>
  );
}

/** The permission-ledger card: the whole-map proportion bar + the three bucket tiles. */
export function CapabilityLedger({
  caps,
  bucket,
  onBucket,
}: {
  readonly caps: readonly CapabilityEntryView[];
  readonly bucket: CapabilityBucket | 'all';
  readonly onBucket: (bucket: CapabilityBucket | 'all') => void;
}): React.JSX.Element {
  const counts = bucketCounts(caps);
  return (
    <div className="cm-ledger">
      <div className="cm-ledger-head">
        <h2 className="cm-ledger-title">Everything Ritona can reach sits in one of three boxes</h2>
        <span className="spacer" aria-hidden="true" />
        {bucket !== 'all' ? (
          <button type="button" className="cm-clear" onClick={() => onBucket('all')}>
            Clear filter
          </button>
        ) : (
          <span className="cm-hint">Choose a box to filter</span>
        )}
      </div>
      <div className="cm-bar" aria-hidden="true">
        {BUCKET_ORDER.map((key) => (
          <span
            key={key}
            data-bucket={key}
            className={bucket === 'all' || bucket === key ? 'seg' : 'seg dim'}
            style={{ flexGrow: Math.max(counts[key], 1) }}
          />
        ))}
      </div>
      <div className="cm-tiles">
        {BUCKET_ORDER.map((key) => (
          <BucketTile
            key={key}
            bucket={key}
            count={counts[key]}
            total={caps.length}
            on={bucket === key}
            onToggle={() => onBucket(bucket === key ? 'all' : key)}
          />
        ))}
      </div>
    </div>
  );
}
